/**
 * @file Drives the entry step of a multi-part Signify fetch: distributor, product search, the
 * staging draft, and Start. Consumer: `SignifyFetchJobEntry`.
 * Selection is decoupled from fetching — unlike the single-part modal, neither picking a product nor
 * changing the distributor calls Signify. Nothing is fetched until Start, which is what lets a list
 * of hundreds be assembled offline.
 * `distributorSearchValue` is derived rather than mirrored, following `useSignifyProductModal`: the
 * typed value is `null` unless the operator is typing, and `''` is a real typed value, so the
 * fallback is `??` and a renamed distributor can never show stale text.
 */

import { useCallback, useContext, useEffect, useState } from 'react';
import { DEFAULT_SIGNIFY_CUSTOMER_ID, SIGNIFY_FETCH_JOB_MAX_INPUTS } from '#types';
import { MessageContext } from '@/providers/MessageContext';
import { uiState } from '@/store/uiState';
import { useSignifyDistributors, useSignifyProductSearch, useStartSignifyFetchJob } from '../../../api';
import { useSignifyFetchJobDraft } from './useSignifyFetchJobDraft';

export const useSignifyFetchJobEntry = ({ targetKey }: { targetKey: string }) => {
  const [selectedDistributor, setSelectedDistributor] = useState<string>('');
  const [typedDistributorSearch, setTypedDistributorSearch] = useState<string | null>(null);
  const [productSearchValue, setProductSearchValue] = useState<string>('');

  const { messageApi } = useContext(MessageContext);
  const {
    distributors: filteredDistributors,
    allDistributors,
    loading: loadingDistributors,
    searchTerm: distributorSearchTerm,
    setSearchTerm: setDistributorSearchTerm,
    refreshDistributors,
    loadDistributorsIfNeeded,
    totalCount: totalDistributors,
    filteredCount: filteredDistributorsCount,
  } = useSignifyDistributors(DEFAULT_SIGNIFY_CUSTOMER_ID);
  const { productOptions, searchingProducts, searchProducts, clearSearch } = useSignifyProductSearch();
  const { starting, startFetchJob } = useStartSignifyFetchJob();
  const { draft, addEntries, commitInputText, removeInput, clearInputs } = useSignifyFetchJobDraft();

  useEffect(() => {
    loadDistributorsIfNeeded();
  }, [loadDistributorsIfNeeded]);

  const distributorSearchValue =
    typedDistributorSearch ?? allDistributors.find((d) => d.uid === selectedDistributor)?.name ?? '';

  const onDistributorSearch = useCallback((value: string) => {
    setTypedDistributorSearch(value);
    setDistributorSearchTerm(value);
  }, [setDistributorSearchTerm]);

  const onDistributorSelect = useCallback((value: string) => {
    setSelectedDistributor(value);
    setTypedDistributorSearch(null);
  }, []);

  const onDistributorClear = useCallback(() => {
    setSelectedDistributor('');
    setTypedDistributorSearch(null);
  }, []);

  const onProductSearch = useCallback((value: string) => {
    setProductSearchValue(value);
    searchProducts(value);
  }, [searchProducts]);

  /**
   * Appends one pick and empties the box for the next one, where the single-part modal writes the
   * product's name into it. The staging list is the only display of what was chosen.
   */
  const onProductSelect = useCallback((value: string) => {
    const product = productOptions.find((p) => p.code === value);
    if (!product) return;

    const plan = addEntries([{ input: product.code, quantity: 1, product }]);
    setProductSearchValue('');
    clearSearch();

    if (plan.exceedsCap) {
      messageApi.warning(`The list is full (${SIGNIFY_FETCH_JOB_MAX_INPUTS} parts). Remove a part to add another.`);
      return;
    }
    if (plan.accepted.length === 0) {
      messageApi.info(`${product.name} is already in the list.`);
    }
  }, [addEntries, clearSearch, messageApi, productOptions]);

  const onProductClear = useCallback(() => {
    setProductSearchValue('');
    clearSearch();
  }, [clearSearch]);

  /**
   * Turns the staged list into a job. Everything the request needs is read off the proxy before the
   * await, and the only post-await touch is the write of the job id, which replaces this step with
   * the progress step and takes the draft with it. A failure leaves the list intact to try again.
   */
  const onStart = useCallback(async () => {
    const distributorId = selectedDistributor;
    const entries = draft.keys.map((key) => {
      const row = draft.byKey[key];
      return {
        input: row.input,
        quantity: row.quantity,
        ...(row.product ? { product: row.product } : {}),
      };
    });
    if (entries.length === 0 || !distributorId) return;

    try {
      const { jobId } = await startFetchJob({
        targetKey,
        customerId: DEFAULT_SIGNIFY_CUSTOMER_ID,
        distributorId,
        entries,
      });
      uiState.signifyFetchJob.activeJobIdByTable[targetKey] = jobId;
    } catch (error) {
      messageApi.error(error instanceof Error ? error.message : 'Failed to start the Signify fetch.');
    }
  }, [draft, messageApi, selectedDistributor, startFetchJob, targetKey]);

  return {
    selectedDistributor,
    distributorSearchValue,
    distributorSearchTerm,
    filteredDistributors,
    filteredDistributorsCount,
    totalDistributors,
    loadingDistributors,
    onDistributorSearch,
    onDistributorSelect,
    onDistributorClear,
    onRefreshDistributors: refreshDistributors,
    productSearchValue,
    productOptions,
    searchingProducts,
    onProductSearch,
    onProductSelect,
    onProductClear,
    draft,
    addEntries,
    commitInputText,
    removeInput,
    clearInputs,
    starting,
    onStart,
  };
};
